import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '@/shared/api/axios';

export interface MasterItem {
  _id: string;
  masterType: string;
  name: string;
  code?: string;
  parentId?: string | null;
  sortOrder: number;
  isActive: boolean;
  meta?: Record<string, unknown>;
}

/**
 * @param isActive Omit to get every row (active and inactive) — what the
 *   admin CRUD list itself needs, since it's the screen that toggles a row
 *   back on. Pass `true` for any dropdown a user picks FROM (a parent
 *   selector, a product's Division/Group/Grade/Pack Size, etc.) — a
 *   deactivated row should disappear from those, which is exactly what
 *   deactivating a master item is supposed to do (db-masters.js's own
 *   contract: "hides an item from new-record dropdowns without touching
 *   existing records that already reference it by id").
 */
export function useMasterItems(masterType: string, parentId?: string | null, isActive?: boolean) {
  return useQuery({
    queryKey: ['masters', masterType, parentId ?? 'root', isActive ?? 'all'],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (parentId !== undefined) params.set('parentId', parentId || '');
      if (isActive !== undefined) params.set('isActive', String(isActive));
      const { data } = await api.get<{ items: MasterItem[] }>(`/api/masters/${masterType}?${params}`);
      return data.items;
    },
    enabled: !!masterType,
  });
}

export function useMasterMutations(masterType: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['masters', masterType] });

  const create = useMutation({
    mutationFn: async (body: { name: string; code?: string; parentId?: string | null; sortOrder?: number }) =>
      (await api.post(`/api/masters/${masterType}`, body)).data,
    onSuccess: invalidate,
  });

  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: string; name?: string; code?: string; sortOrder?: number }) =>
      (await api.patch(`/api/masters/${masterType}/${id}`, body)).data,
    onSuccess: invalidate,
  });

  const toggleActive = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) =>
      (await api.post(`/api/masters/${masterType}/${id}/${isActive ? 'activate' : 'deactivate'}`)).data,
    onSuccess: invalidate,
  });

  return { create, update, toggleActive };
}

// ── Special masters (real-column kinds) ─────────────────────────────────────

export interface SpecialMasterItem {
  _id: string;
  name: string;
  isActive: boolean;
  [key: string]: unknown;
}

export function useSpecialMasterItems(kind: string) {
  return useQuery({
    queryKey: ['masters-special', kind],
    queryFn: async () => (await api.get<{ items: SpecialMasterItem[] }>(`/api/masters/special/${kind}`)).data.items,
    enabled: !!kind,
  });
}

export function useSpecialMasterMutations(kind: string) {
  const qc = useQueryClient();
  const invalidate = () => qc.invalidateQueries({ queryKey: ['masters-special', kind] });

  const create = useMutation({
    mutationFn: async (body: Record<string, unknown>) => (await api.post(`/api/masters/special/${kind}`, body)).data,
    onSuccess: invalidate,
  });
  const update = useMutation({
    mutationFn: async ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      (await api.patch(`/api/masters/special/${kind}/${id}`, body)).data,
    onSuccess: invalidate,
  });
  const deactivate = useMutation({
    mutationFn: async (id: string) => (await api.post(`/api/masters/special/${kind}/${id}/deactivate`)).data,
    onSuccess: invalidate,
  });
  // Permanent delete — quotation terms only, Admin / Super Admin only (server-enforced).
  const bulkDelete = useMutation({
    mutationFn: async (ids: string[]) => (await api.post<{ deleted: number }>(`/api/masters/special/${kind}/bulk-delete`, { ids, confirm: true })).data,
    onSuccess: invalidate,
  });

  return { create, update, deactivate, bulkDelete };
}
